import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { LanguageSelector } from '../components/LanguageSelector';
import { 
  UtensilsCrossed, Users, ClipboardList, Printer, Building2, Smartphone,
  ArrowRight, Heart, Shield, ChevronRight, Menu, X
} from 'lucide-react';
import { useState } from 'react';

const LandingPage = () => {
  const { t, tObj, getTranslation } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const features = tObj('landing.features.items') || [];
  const featureIcons = [UtensilsCrossed, ClipboardList, Shield, Printer, Building2, Smartphone];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo - cliccabile per home */}
            <Link to="/" className="flex items-center">
              <img 
                src="/logo-horizontal-trimmed.png" 
                alt="ServiCare" 
                className="h-10 sm:h-12 object-contain"
                loading="lazy"
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-6">
              <a href="#features" className="text-slate-600 hover:text-slate-900 text-sm font-medium">
                {t('landing.features.title')}
              </a>
              <Link to="/privacy" className="text-slate-600 hover:text-slate-900 text-sm font-medium">
                {t('common.nav.privacy')}
              </Link>
              <Link to="/terms" className="text-slate-600 hover:text-slate-900 text-sm font-medium">
                {t('common.nav.terms')}
              </Link>
              <LanguageSelector variant="minimal" />
              <Link to="/login">
                <Button className="bg-blue-700 hover:bg-blue-800">
                  {t('common.nav.login')}
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex items-center gap-2 md:hidden">
              <LanguageSelector variant="minimal" />
              <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 py-4 px-4 space-y-3">
            <a 
              href="#features" 
              className="block py-2 text-slate-600 hover:text-slate-900"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('landing.features.title')}
            </a>
            <Link 
              to="/privacy" 
              className="block py-2 text-slate-600 hover:text-slate-900"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('common.nav.privacy')}
            </Link>
            <Link 
              to="/terms" 
              className="block py-2 text-slate-600 hover:text-slate-900"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('common.nav.terms')}
            </Link>
            <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
              <Button className="w-full bg-blue-700 hover:bg-blue-800 mt-2">
                {t('common.nav.login')}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 sm:pt-32 sm:pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full text-blue-700 text-sm font-medium mb-6">
              <Shield className="w-4 h-4" />
              <span>Swiss Quality Software</span>
            </div>
            
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-6">
              {t('landing.hero.title')}
            </h1>
            
            {/* Subtitle */}
            <p className="text-lg sm:text-xl text-slate-600 mb-8 leading-relaxed max-w-3xl mx-auto">
              {t('landing.hero.subtitle')}
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/login">
                <Button size="lg" className="bg-blue-700 hover:bg-blue-800 text-base px-8 h-12">
                  {t('landing.hero.cta')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <a href="#features">
                <Button size="lg" variant="outline" className="text-base px-8 h-12">
                  {t('landing.hero.ctaSecondary')}
                </Button>
              </a>
            </div>
          </div>

          {/* Hero Image/App Preview - Generic Mockup */}
          <div className="mt-16 max-w-5xl mx-auto">
            <div className="relative bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl p-3 sm:p-6 shadow-2xl">
              <div className="aspect-[16/9] bg-white rounded-xl shadow-inner overflow-hidden">
                {/* Simulated App Interface */}
                <div className="h-full flex">
                  {/* Sidebar mockup */}
                  <div className="w-16 sm:w-48 bg-slate-50 border-r border-slate-100 p-3 hidden sm:block">
                    <div className="space-y-3">
                      <div className="h-8 bg-slate-200 rounded-lg animate-pulse"></div>
                      <div className="h-6 bg-blue-100 rounded-lg w-3/4"></div>
                      <div className="h-6 bg-slate-100 rounded-lg w-2/3"></div>
                      <div className="h-6 bg-slate-100 rounded-lg w-4/5"></div>
                      <div className="h-6 bg-slate-100 rounded-lg w-1/2"></div>
                    </div>
                  </div>
                  {/* Main content mockup */}
                  <div className="flex-1 p-4 sm:p-6">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <div className="h-6 sm:h-8 bg-slate-200 rounded-lg w-40 sm:w-56 mb-2"></div>
                        <div className="h-4 bg-slate-100 rounded w-32 sm:w-40"></div>
                      </div>
                      <div className="flex gap-2">
                        <div className="h-8 w-20 bg-blue-100 rounded-lg"></div>
                        <div className="h-8 w-20 bg-amber-100 rounded-lg"></div>
                      </div>
                    </div>
                    {/* Week calendar */}
                    <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-6">
                      {['L', 'M', 'M', 'G', 'V', 'S', 'D'].map((d, i) => (
                        <div key={i} className={`h-10 sm:h-12 rounded-lg flex items-center justify-center text-xs sm:text-sm font-medium ${i === 5 ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
                          {d}
                        </div>
                      ))}
                    </div>
                    {/* Meal cards */}
                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                      {['Antipasto', 'Primo', 'Secondo', 'Dolce'].map((meal, i) => (
                        <div key={i} className="bg-slate-50 rounded-xl p-3 sm:p-4 border border-slate-100">
                          <div className="text-xs sm:text-sm font-medium text-slate-700 mb-2">{meal}</div>
                          <div className="h-4 bg-slate-200 rounded w-full mb-1.5"></div>
                          <div className="h-3 bg-slate-100 rounded w-2/3"></div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-blue-600/10 rounded-full blur-2xl" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-amber-500/10 rounded-full blur-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
              {t('landing.features.title')}
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              {t('landing.features.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {features.map((feature, index) => {
              const Icon = featureIcons[index] || UtensilsCrossed;
              return (
                <div 
                  key={index}
                  className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-slate-100"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-5">
                    <Icon className="w-6 h-6 text-blue-700" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">
                    {getTranslation(feature.title)}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {getTranslation(feature.description)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-blue-700 to-blue-800 rounded-3xl p-8 sm:p-12 text-white">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              {t('landing.cta.title')}
            </h2>
            <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
              {t('landing.cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/login">
                <Button size="lg" className="bg-white text-blue-700 hover:bg-blue-50 text-base px-8 h-12">
                  {t('landing.cta.button')}
                  <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div className="md:col-span-2">
              <Link to="/" className="inline-block mb-4">
                <img 
                  src="/logo-horizontal-trimmed.png" 
                  alt="ServiCare" 
                  className="h-14 sm:h-16 object-contain"
                  loading="lazy"
                />
              </Link>
              <p className="text-slate-400 text-sm max-w-md">
                {t('landing.hero.subtitle')}
              </p>
            </div>

            {/* Legal Links */}
            <div>
              <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider text-slate-400">Legal</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/privacy" className="text-slate-300 hover:text-white text-sm transition-colors">
                    {t('common.nav.privacy')}
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-slate-300 hover:text-white text-sm transition-colors">
                    {t('common.nav.terms')}
                  </Link>
                </li>
                <li>
                  <Link to="/cookies" className="text-slate-300 hover:text-white text-sm transition-colors">
                    {t('common.nav.cookies')}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider text-slate-400">Contact</h4>
              <ul className="space-y-3 text-sm text-slate-300">
                <li>info@servicare.ch</li>
                <li>Switzerland</li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} ServiCare. {t('common.footer.rights')}.
            </p>
            <p className="text-slate-400 text-sm flex items-center gap-1">
              {t('common.footer.madeWith')} <Heart className="w-4 h-4 text-red-500 fill-red-500" /> {t('common.footer.inSwitzerland')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
